import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  useTheme,
  MenuItem,
} from "@mui/material";
import dayjs from "dayjs";
import axios from "axios";
// import SimpleTable from "../../components/SimpleTable";
import PurchaseTable from "../../components/PurchaseTable";
import { tokens } from "../../theme";

const PurchaseReport = () => {
  const theme = useTheme();
  const today = dayjs().format("YYYY-MM-DD");
  const colors = tokens(theme.palette.mode);

  const [purchases, setPurchases] = useState([]);
  const [summary, setSummary] = useState({});
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [supplierId, setSupplierId] = useState("");
  const [itemId, setItemId] = useState("");
  const [warehouseId, setWarehouseId] = useState("");

  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);

 
  const normalizeDateForAPI = (dateString) => dateString || "";

  const fetchSuppliers = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/customer`
      );
      const suppliersOnly = (res.data || []).filter(
        (c) => c.type === "supplier"
      );
      setSuppliers(suppliersOnly);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/item`);
      setItems(res.data || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const fetchWarehouses = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/warehouse`
      );
      setWarehouses(res.data || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    fetchSuppliers();
    fetchItems();
    fetchWarehouses();
  }, [fetchSuppliers, fetchItems, fetchWarehouses]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {};

      // Use the normalized dates
      if (startDate) payload.startDate = normalizeDateForAPI(startDate);
      if (endDate) payload.endDate = normalizeDateForAPI(endDate);

      // if (supplierId) payload.customerId = supplierId;
      if (supplierId) payload.supplierId = supplierId;

      if (itemId) payload.itemId = itemId;
      if (warehouseId) payload.warehouseId = warehouseId;

      // Debug: log what's being sent
      console.log("Sending payload:", payload);

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/purchase/report`,
        payload
      );

      // Map purchases to table rows, keeping purchase-level totals and items nested
      const rows = (res.data.report || []).map((p) => ({
        id: p.purchaseId,
        purchaseDate: p.purchaseDate,
        supplier: p.supplier,
        warehouse: p.warehouse,
        status: p.status,
        totalVatedAfterDiscount: p.totalVatedAfterDiscount,
        vat: p.vat,
        withholdingAmount: p.withholdingAmount,
        items: p.items,
      }));

      // Calculate summary totals
      const totalVAT = (res.data.report || []).reduce(
        (sum, p) => sum + (p.vat || 0),
        0
      );
      const totalWithholding = (res.data.report || []).reduce(
        (sum, p) => sum + (p.withholdingAmount || 0),
        0
      );

      setPurchases(rows);
      setSummary({
        purchaseCount: res.data.summary?.purchaseCount || 0,
        totalQuantity: res.data.summary?.totalQuantity || 0,
        totalPrice: (res.data.report || []).reduce(
          (sum, p) => sum + (p.totalVatedAfterDiscount || 0),
          0
        ),
        totalVAT,
        // totalExcise: 0,
        totalWithholding,
      });
    } catch (err) {
      console.error(err);
      setPurchases([]);
      setSummary({
        purchaseCount: 0,
        totalQuantity: 0,
        totalPrice: 0,
        totalVAT: 0,
        // totalExcise: 0,
        totalWithholding: 0,
      });
    }
    setLoading(false);
  }, [startDate, endDate, supplierId, itemId, warehouseId]);



  useEffect(() => {
  fetchReport();
}, [fetchReport]);

  const columns = [
    {
      field: "purchaseDate",
      headerName: "Purchase Date",
      width: 150,
      //     valueFormatter: ({ value }) =>
      // dayjs.utc(value).format("YYYY-MM-DD"),
      valueFormatter: ({ value }) => dayjs(value).format("YYYY-MM-DD"),
    },
    { field: "supplier", headerName: "Supplier", width: 160 },
    { field: "warehouse", headerName: "Warehouse", width: 160 },
    { field: "status", headerName: "Status", width: 120 },
    {
      field: "totalVatedAfterDiscount",
      headerName: "Total After Discount",
      width: 130,
    },
    { field: "vat", headerName: "VAT", width: 100 },
    { field: "withholdingAmount", headerName: "Withholding", width: 130 },
    {
      field: "items",
      headerName: "Items",
      width: 620,
      sortable: false,
      filterable: false,
      renderHeader: () => (
        <Box sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
          {/* Single Header for the entire column */}
          <Box
            display="grid"
            gridTemplateColumns="200px 70px 70px 100px 70px 80px"
            gap={0.5}
          >
            <Box>Item Name</Box>
            <Box textAlign="center">Unit</Box>
            <Box textAlign="right">Qty</Box>
            <Box textAlign="right">Unit Price</Box>
            <Box textAlign="right">Bonus</Box>
            <Box textAlign="center">Sponsor</Box>
          </Box>
        </Box>
      ),
      renderCell: (params) => {
        const items = params.value || [];
        if (items.length === 0)
          return <Typography color="text.secondary">-</Typography>;

        return (
          <Box sx={{ py: 0.5 }}>
            {items.map((item: any, idx: number) => (
              <Box
                key={item.itemId}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "200px 70px 70px 100px 70px 80px",
                  gap: 0.5,
                  alignItems: "center",
                  py: 0.35,
                  fontSize: "0.8rem",
                  borderBottom: idx < items.length - 1 ? "1px dashed" : "none",
                  borderColor: "divider",
                }}
              >
                <Box
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontWeight: 500,
                  }}
                  title={item.name}
                >
                  {item.name}
                </Box>
                <Box textAlign="center" color="text.secondary">
                  {item.unit || "-"}
                </Box>
                <Box textAlign="right" fontWeight="medium">
                  {item.quantity}
                </Box>
                <Box textAlign="right">
                  {Number(item.unitPrice).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Box>
                <Box
                  textAlign="right"
                  color={item.bonus > 0 ? "success.main" : "inherit"}
                >
                  {item.bonus > 0 ? item.bonus : "-"}
                </Box>
                <Box textAlign="center" color="info.main">
                  {item.sponsor || "-"}
                </Box>
              </Box>
            ))}
          </Box>
        );
      },
    },
  ];

  const gridSx = {
    "& .MuiDataGrid-root": { border: "none" },
    "& .MuiDataGrid-cell": { borderBottom: "none" },
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: colors.blueAccent[700],
      borderBottom: "none",
    },
    "& .MuiDataGrid-virtualScroller": {
      backgroundColor: colors.primary[400],
    },
    "& .MuiDataGrid-footerContainer": {
      borderTop: "none",
      backgroundColor: colors.blueAccent[700],
    },
    "& .MuiCheckbox-root": {
      color: `${colors.greenAccent[200]} !important`,
    },
    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
      color: `${colors.grey[100]} !important`,
    },
  };

  return (
    <Box p={3}>
      <Typography variant="h4" mb={2}>
        Purchase Report
      </Typography>

      <Box display="flex" flexWrap="wrap" gap={2} mb={2} alignItems="center">
        <TextField
          label="Start Date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="End Date"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          select
          label="Supplier"
          value={supplierId}
          onChange={(e) => setSupplierId(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All</MenuItem>
          {suppliers.map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {s.name}
            </MenuItem>
          ))}
        </TextField>
       
        <TextField
          select
          label="Warehouse"
          value={warehouseId}
          onChange={(e) => setWarehouseId(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All</MenuItem>
          {warehouses.map((w) => (
            <MenuItem key={w.id} value={w.id}>
              {w.name}
            </MenuItem>
          ))}
        </TextField>
        <Button variant="contained" onClick={fetchReport} disabled={loading}>
          {loading ? "Loading..." : "Generate Report"}
        </Button>
      </Box>

      <Box
        display="flex"
        flexWrap="wrap"
        gap={2}
        mb={3}
        sx={{
          backgroundColor: colors.primary[400],
          p: 2,
          borderRadius: 2,
          boxShadow: 2,
        }}
      >
        <Box
          flex={1}
          minWidth={140}
          textAlign="center"
          p={1}
          sx={{ backgroundColor: colors.blueAccent[700], borderRadius: 1 }}
        >
          <Typography variant="subtitle2">Purchase Count</Typography>
          <Typography variant="h6" fontWeight="bold">
            {summary.purchaseCount || 0}
          </Typography>
        </Box>

        <Box
          flex={1}
          minWidth={140}
          textAlign="center"
          p={1}
          sx={{ backgroundColor: colors.blueAccent[700], borderRadius: 1 }}
        >
          <Typography variant="subtitle2">Total Quantity</Typography>
          <Typography variant="h6" fontWeight="bold">
            {summary.totalQuantity || 0}
          </Typography>
        </Box>

        <Box
          flex={1}
          minWidth={140}
          textAlign="center"
          p={1}
          sx={{ backgroundColor: colors.blueAccent[700], borderRadius: 1 }}
        >
          <Typography variant="subtitle2">Total Price</Typography>
          <Typography variant="h6" fontWeight="bold">
            {summary.totalPrice || 0}
          </Typography>
        </Box>

        <Box
          flex={1}
          minWidth={140}
          textAlign="center"
          p={1}
          sx={{ backgroundColor: colors.blueAccent[700], borderRadius: 1 }}
        >
          <Typography variant="subtitle2">Total VAT</Typography>
          <Typography variant="h6" fontWeight="bold">
            {summary.totalVAT || 0}
          </Typography>
        </Box>

      

        <Box
          flex={1}
          minWidth={140}
          textAlign="center"
          p={1}
          sx={{ backgroundColor: colors.blueAccent[700], borderRadius: 1 }}
        >
          <Typography variant="subtitle2">Total Withholding</Typography>
          <Typography variant="h6" fontWeight="bold">
            {summary.totalWithholding || 0}
          </Typography>
        </Box>
      </Box>

      <PurchaseTable
        rows={purchases}
        summary={summary}
        themeColors={tokens(theme.palette.mode)}
      />
    </Box>
  );
};

export default PurchaseReport;
